import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Mail, Bell, BellOff, Loader2, Send, Users, Refrigerator, Repeat } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function subscribeToPush() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) throw new Error("Push isn't supported on this browser — on iPhone, add Sorted to your Home Screen first");
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notifications were blocked — allow them in your phone settings");
  const { data } = await api.get("/push/vapid-public-key");
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(data.key) });
  await api.post("/push/subscribe", { subscription: sub.toJSON() });
  return sub;
}

export default function NotificationSettings() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/notifications/prefs"); setPrefs(data); } catch { /* optional */ }
    try {
      if ("serviceWorker" in navigator) { const reg = await navigator.serviceWorker.getRegistration(); const sub = await reg?.pushManager?.getSubscription(); setSubscribed(!!sub); }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const enable = async () => {
    setBusy(true);
    try { await subscribeToPush(); setSubscribed(true); toast.success("Notifications on — Millie will only nudge when it's useful"); await load(); }
    catch (err) { toast.error(err.message || "Couldn't turn on notifications"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration(); const sub = await reg?.pushManager?.getSubscription();
      if (sub) { await api.post("/push/unsubscribe", { subscription: sub.toJSON() }); await sub.unsubscribe(); }
      setSubscribed(false); toast("Notifications off on this device"); await load();
    } finally { setBusy(false); }
  };
  const save = async (next) => {
    setPrefs(next);
    try { await api.put("/notifications/prefs", { partner_nudges: next.partner_nudges, fridge_alerts: next.fridge_alerts, fridge_alert_time: next.fridge_alert_time, timezone: next.timezone, ritual_reminders: next.ritual_reminders, ritual_reminder_time: next.ritual_reminder_time, recap_email: next.recap_email }); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const test = async () => { try { const { data } = await api.post("/push/test"); toast.success(data.sent ? "Sent! Check your notifications" : "No device subscribed yet — turn notifications on first"); } catch { toast.error("Couldn't send a test"); } };

  if (!prefs) return null;

  return (
    <div className="bg-white rounded-3xl border-2 border-[#FFF3F0] shadow-soft p-4" data-testid="notification-settings">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-[#FBE6DC] flex items-center justify-center text-orange-500"><Bell size={18} strokeWidth={2.5} /></span>
        <div className="flex-1">
          <p className="font-heading font-semibold text-sorted-ink">Notifications</p>
          <p className="text-xs text-sorted-slate">{prefs.subscribed_devices} device{prefs.subscribed_devices === 1 ? "" : "s"} · on iPhone, add Sorted to your Home Screen first</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        {subscribed ? (
          <button onClick={disable} disabled={busy} data-testid="push-disable" className="bg-white border-2 border-[#FFF3F0] text-sorted-ink text-sm font-bold py-3 rounded-full flex items-center justify-center gap-1.5">{busy ? <Loader2 size={15} className="animate-spin" /> : <BellOff size={15} strokeWidth={2.5} />} Turn off here</button>
        ) : (
          <button onClick={enable} disabled={busy} data-testid="push-enable" className="bg-sorted-rose text-white text-sm font-bold py-3 rounded-full flex items-center justify-center gap-1.5">{busy ? <Loader2 size={15} className="animate-spin" /> : <Bell size={15} strokeWidth={2.5} />} Turn on</button>
        )}
        <button onClick={test} data-testid="push-test" className="bg-white border-2 border-[#FFF3F0] text-sorted-ink text-sm font-bold py-3 rounded-full flex items-center justify-center gap-1.5"><Send size={15} strokeWidth={2.5} /> Send a test</button>
      </div>

      <div className="mt-4 space-y-3">
        <Toggle icon={Users} label="Partner nudges" hint="When someone starts cooking or finishes the shop" checked={prefs.partner_nudges} onChange={(v) => save({ ...prefs, partner_nudges: v })} testId="pref-partner" />
        <Toggle icon={Refrigerator} label="Morning fridge check" hint="What needs using up today" checked={prefs.fridge_alerts} onChange={(v) => save({ ...prefs, fridge_alerts: v })} testId="pref-fridge" />
        {prefs.fridge_alerts && (
          <div className="flex items-center justify-between pl-12">
            <label className="text-sm font-semibold text-sorted-ink">Alert time</label>
            <input type="time" value={prefs.fridge_alert_time} onChange={(e) => save({ ...prefs, fridge_alert_time: e.target.value })} data-testid="pref-fridge-time"
              className="bg-muted rounded-2xl px-3 py-2 text-sm font-bold text-sorted-ink outline-none focus:ring-2 focus:ring-sorted-peach" />
          </div>
        )}
        <Toggle icon={Mail} label="Sunday recap email" hint="Little Wins + next week's plan card in your inbox, 3pm Sunday" checked={prefs.recap_email !== false} onChange={(v) => save({ ...prefs, recap_email: v })} testId="pref-recap-email" />
        <Toggle icon={Repeat} label="Ritual & event reminders" hint="The night before Taco Tuesday, soccer, Nan's visit…" checked={prefs.ritual_reminders} onChange={(v) => save({ ...prefs, ritual_reminders: v })} testId="pref-ritual" />
        {prefs.ritual_reminders && (
          <div className="flex items-center justify-between pl-12">
            <label className="text-sm font-semibold text-sorted-ink">Reminder time</label>
            <input type="time" value={prefs.ritual_reminder_time} onChange={(e) => save({ ...prefs, ritual_reminder_time: e.target.value })} data-testid="pref-ritual-time"
              className="bg-muted rounded-2xl px-3 py-2 text-sm font-bold text-sorted-ink outline-none focus:ring-2 focus:ring-sorted-peach" />
          </div>
        )}
      </div>
    </div>
  );
}

function Toggle({ icon: Icon, label, hint, checked, onChange, testId }) {
  return (
    <button onClick={() => onChange(!checked)} data-testid={testId} className="w-full flex items-center gap-3 text-left">
      <span className="w-9 h-9 rounded-2xl bg-muted flex items-center justify-center text-sorted-ink shrink-0"><Icon size={17} strokeWidth={2.5} /></span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-bold text-sorted-ink">{label}</span>
        <span className="block text-xs text-sorted-slate">{hint}</span>
      </span>
      <span className={`w-12 h-7 rounded-full p-1 transition-colors ${checked ? "bg-sorted-rose" : "bg-[#E9DDE0]"}`}>
        <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : ""}`} />
      </span>
    </button>
  );
}
